/**
 * Computes the aggregate bounding box from an array of features.
 * Each feature may have a `bbox` property: [xMin, yMin, xMax, yMax].
 *
 * @param {Array} features - Array of feature objects with optional `bbox` property
 * @returns {{ extent: number[]|null, missingCount: number, totalCount: number }}
 *   - extent: [xMin, yMin, xMax, yMax] or null if all features lack bbox
 *   - missingCount: Number of features without bbox data
 *   - totalCount: Total features in input array
 */
export function computeAggregateBbox(features) {
  let xMin = Infinity, yMin = Infinity, xMax = -Infinity, yMax = -Infinity;
  let missingCount = 0;
  const totalCount = features.length;

  features.forEach(f => {
    if (!f.bbox || !Array.isArray(f.bbox) || f.bbox.length !== 4) {
      missingCount++;
      return;
    }
    const [fxMin, fyMin, fxMax, fyMax] = f.bbox;
    if (fxMin < xMin) xMin = fxMin;
    if (fyMin < yMin) yMin = fyMin;
    if (fxMax > xMax) xMax = fxMax;
    if (fyMax > yMax) yMax = fyMax;
  });

  if (missingCount === totalCount) {
    return { extent: null, missingCount, totalCount };
  }

  return { extent: [xMin, yMin, xMax, yMax], missingCount, totalCount };
}

/**
 * Adds percentage-based padding around an extent.
 * For degenerate extents (single point where dx < 1 and dy < 1 in map units),
 * applies a fixed buffer instead.
 *
 * @param {number[]} extent - [xMin, yMin, xMax, yMax] in EPSG:3857
 * @param {Object} options - Configuration object
 * @param {number} options.paddingFactor - Padding factor as decimal (default 0.1 = 10%)
 * @param {number} options.minBuffer - Buffer in map units for degenerate extents (default 100)
 * @returns {number[]} Padded extent [xMin, yMin, xMax, yMax]
 */
export function padExtent(extent, options = {}) {
  const { paddingFactor = 0.1, minBuffer = 100 } = options;

  const [xMin, yMin, xMax, yMax] = extent;
  let dx = xMax - xMin;
  let dy = yMax - yMin;

  // Degenerate case: single point or near-zero extent
  if (dx < 1 && dy < 1) {
    return [
      xMin - minBuffer,
      yMin - minBuffer,
      xMax + minBuffer,
      yMax + minBuffer
    ];
  }

  return [
    xMin - dx * paddingFactor,
    yMin - dy * paddingFactor,
    xMax + dx * paddingFactor,
    yMax + dy * paddingFactor
  ];
}
